import type React from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Spin,
  Table,
  TableColumnsType,
  Button,
  Tag,
  TableProps,
} from "antd";
import {
  RiseOutlined,
  ClockCircleOutlined,
  EditOutlined,
  HomeOutlined,
  FolderOpenOutlined,
  ShoppingCartOutlined,
  StopOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useEffect, useState } from "react";
import { BaseApi } from "../base.api";
import { Console, count, log } from "console";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

const Dashboard: React.FC = () => {
  interface selectedStatusType {
    status: number;
    count: number;
  }
  interface DataType {
    name: string;
    subscription: any;
    subscriptionDeadline: any;
  }
  interface Vacancy {
    month: number;
    year: number;
    count: number;
  }
  const [vacancy, setVacancy] = useState<Vacancy[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<selectedStatusType[]>(
    []
  );
  const [selectedYear, setSelectedYear] = useState("2020");
  const [loading, setLoading] = useState<boolean>(true);
  const [vacancyYear, setVacancyYear] = useState("");
  const [vacancyMonth, setVacancyMonth] = useState<Vacancy[]>([]);
  const [subscription, setSubscription] = useState<DataType[]>([]);
  const [premiumSources, setPremiumSources] = useState<[]>([]);
  useEffect(() => {
    handleMonthData(selectedYear);
    handleSelectStatus();
    handleYearData();
    handleSubscription();
    getPremiumSource();
  }, []);
  const statusData = [
    {
      status: 2,
      title: "Active",
      icon: <RiseOutlined />,
      color: "#1890ff",
    },
    {
      status: 3,
      title: "Closed",
      icon: <ClockCircleOutlined />,
      color: "#faad14",
    },
    {
      status: 1,
      title: "Drafted",
      icon: <EditOutlined />,
      color: "#52c41a",
    },
    {
      status: 6,
      title: "Internal",
      icon: <HomeOutlined />,
      color: "#f5222d",
    },
    {
      status: 5,
      title: "Archive",
      icon: <FolderOpenOutlined />,
      color: "#722ed1",
    },
    {
      status: 8,
      title: "Ordered",
      icon: <ShoppingCartOutlined />,
      color: "#722ed1",
    },
    {
      status: 9,
      title: "Rejected",
      icon: <StopOutlined />,
      color: "#722ed1",
    },
    {
      status: 7,
      title: "Removed",
      icon: <DeleteOutlined />,
      color: "#722ed1",
    },
  ];
  function getStatus(status: number) {
    switch (status) {
      case 1:
        return "Drafted";
      case 2:
        return "Active";
      case 3:
        return "Closed";
      case 5:
        return "Archived";
      case 6:
        return "Internal";
      case 7:
        return "Deleted";
      case 8:
        return "Ordered";
      case 9:
        return "Rejected";
    }
  }
  const handleSelectStatus = async () => {
    const response = await new BaseApi().fetchData(
      "/job-service-v2/jobs/counts/by-status"
    );
    setSelectedStatus(response?.data);
    setLoading(false);
  };
  const getCountByStatus = (status: any) => {
    const data = selectedStatus.find((item) => item.status === status);
    return data ? data?.count : 0;
  };
  const handleMonthData = async (year: any) => {
    setSelectedYear(year);

    const response = await new BaseApi().fetchData(
      "/candidate-service/statistics/applications/by-month/for-admin"
    );
    setVacancy(response?.data.data);
    const data = response?.data.data.filter((item: any) => item.year == year);
    setVacancyMonth(data);
  };
  const handleYearData = async () => {
    const response = await new BaseApi().fetchData(
      "/candidate-service/statistics/applications/by-month/for-admin"
    );
    const yearData = response?.data.data.reduce(
      (acc: Record<number, number>, data: any) => {
        acc[data.year] = (acc[data.year] || 0) + data.count;
        return acc;
      },
      {}
    );
    setVacancyYear(yearData);
  };

  const getPremiumSource = async () => {
    setLoading(true);
    try {
      const response = await new BaseApi().fetchData(
        "job-service-v2/jobs/admin/ads?approved=true"
      );
      const now = new Date();
      if (response?.data.length > 0) {
        const companyData = await new BaseApi().fetchData(
          "/user-service-v2/companies"
        );
        await response?.data.forEach((item: any) => {
          const expirestAt = new Date(item.expiresAt);
          const diffTime = expirestAt.getTime() - now.getTime();
          const diffDay = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          item.lastDay = expirestAt.toString().slice(0, 10);
          item.diffDay = diffDay;
          const matchedCompany: any = companyData?.data.find(
            (data: any) => data._id === item.companyId
          );
          if (matchedCompany) {
            item.name = matchedCompany.name;
          }
        });
      }
      setPremiumSources(response?.data);
    } catch (error) {
      console.error("Premium source fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const monthNames = [
    "Yanvar",
    "Fevral",
    "Mart",
    "Aprel",
    "May",
    "İyun",
    "İyul",
    "Avqust",
    "Sentyabr",
    "Oktyabr",
    "Noyabr",
    "Dekabr",
  ];

  const labelsMonth = vacancyMonth.map(
    (data) => `${monthNames[data.month - 1]}`
  );
  const countMonth = vacancyMonth.map((data: any) => data.count);
  const monthsData = {
    labels: labelsMonth,
    datasets: [
      {
        label: "Vacancy count",
        data: countMonth,
        borderColor: "rgba(255, 99, 132, 1)",
        backgroundColor: "rgba(255, 99, 132, 0.2)",
      },
    ],
  };
  const labelsYear = Object.entries(vacancyYear).map(([key]) => key);
  const countYear = Object.entries(vacancyYear).map(([key, value]) => value);

  const yearsData = {
    labels: labelsYear,
    datasets: [
      {
        label: "Vacancy count",
        data: countYear,
        borderColor: "rgba(255, 99, 132, 1)",
        backgroundColor: "rgba(255, 99, 132, 0.2)",
      },
    ],
  };

  const options: any = {
    responsive: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            return ` ${context.raw} count`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
        },
      },
      y: {
        title: {
          display: true,
          text: "Müraciətlərin sayı",
        },
        beginAtZero: true,
      },
    },
  };

  const convertToDateStr = (data: any) => {
    const date = new Date(data);
    const newDateFormat = date.toString().slice(0, 10);
    return newDateFormat;
  };
  const handleSubscription = async () => {
    const response = await new BaseApi().fetchData(
      "/user-service-v2/companies?expiring=true"
    );

    const now = new Date();
    if (response?.data) {
      response.data.forEach((item: any) => {
        const lastDate = new Date(item.subscription.subscriptionDeadline);
        const diffTime = lastDate.getTime() - now.getTime();
        const diffDay = Math.round(diffTime / (1000 * 60 * 60 * 24));
        item.dayUntilDeadline = diffDay;
        item.endDayStr = lastDate.toString().slice(0, 10);
      });
      setSubscription(response?.data);
    }
  };
  const columns: TableColumnsType<DataType> = [
    {
      title: "Name",
      key: "name",
      width: 400,
      render: (record: any) => (
        <a href={"/main/companies/" + record._id}>{record.name || "N/A"}</a>
      ),
    },
    {
      title: "Domain",
      width: 350,
      render: (record: any) => (
        <a href={"https://" + record.domain} target="_blank">
          {record.domain}
        </a>
      ),
      key: "domain",
    },
    {
      title: "Plan",
      width: 300,
      render: (record: any) => (
        <Tag color="purple">
          {getPlan(record.subscription.subscriptionType)}
        </Tag>
      ),
      key: "plan",
    },
    {
      title: "Subscription Deadline",
      key: "subscription",
      defaultSortOrder: "ascend",
      render: (record: any) =>
        record.subscription &&
        record.subscription.subscriptionDeadline && (
          <Tag color={"red"}>
            {`${record.endDayStr} / ${record.dayUntilDeadline} day`}
          </Tag>
        ),
      sorter: (a, b) =>
        new Date(a.subscription?.subscriptionDeadline).getTime() -
        new Date(b.subscription?.subscriptionDeadline).getTime(),
    },
  ];
  const premiumJobsColumn = [
    {
      title: "Title",
      key: "title",
      render: (record: any) => (
        <div
          style={{
            maxWidth: 150,
          }}
        >
          {record.title}
        </div>
      ),
    },
    {
      title: "Company Name",
      key: "name",
      dataIndex: "name",
    },
    {
      title: "Premium Type",
      key: "type",
      render: (record: any) =>
        record.isKingAd === true ? (
          <Tag
            color="purple"
            style={{ width: "30%", textAlign: "center", fontStyle: "italic" }}
          >
            King
          </Tag>
        ) : record.isProAd === true ? (
          <Tag
            color="blue"
            style={{ width: "30%", textAlign: "center", fontStyle: "italic" }}
          >
            Pro
          </Tag>
        ) : (
          ""
        ),
    },
    {
      title: "Posted Date ",
      key: "postedAt",
      render: (record: any) => <Tag>{convertToDateStr(record.postedAt)}</Tag>,
    },
    {
      title: "Expires At",
      key: "expiresAt",
      render: (record: any) =>
        record.diffDay < 0 ? (
          <Tag color="red">
            {record.lastDay} / {record.diffDay} day
          </Tag>
        ) : (
          <Tag color="green">
            {record.lastDay} / {record.diffDay} day
          </Tag>
        ),
    },
    {
      title: "Status",
      key: "status",
      render: (record: any) =>
        getStatus(record.status) ? <p>{getStatus(record.status)}</p> : "N/A",
    },
    {
      key: "isApproved",
      render: (record: any) =>
        record.isApproved === true ? (
          <Tag color="green">Approved</Tag>
        ) : (
          <Tag color="red">UnApproved</Tag>
        ),
    },
  ];
  const onChange: TableProps<DataType>["onChange"] = (
    pagination,
    filters,
    sorter,
    extra
  ) => {
    console.log("params", pagination, filters, sorter, extra);
  };

  const getPlan = (type: number) => {
    switch (type) {
      case 1:
        return "Trial";
      case 2:
        return "Monthly";
      case 3:
        return "Yearly";
      case 4:
        return "PayAsYouGo";
      case 5:
        return "MonthlyUnlimited";
      case 6:
        return "YearlyUnlimited";
      case 7:
        return "Hybrid";
      case 8:
        return "Flex";
      default:
        return "Unknown Subscription Type";
    }
  };

  return (
    <>
      {loading ? (
        <Spin
          size="large"
          tip="Loading..."
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        />
      ) : (
        <div style={{ padding: "" }}>
          <h1
            style={{
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            Vacancy count by status
          </h1>
          <Row gutter={[16, 16]}>
            {statusData.map((status) => (
              <Col xs={24} sm={12} md={8} lg={6} xl={3} key={status.status}>
                <Card>
                  <Statistic
                    title={status.title}
                    value={getCountByStatus(status.status)}
                    valueStyle={{ color: status.color }}
                    prefix={status.icon}
                  />
                </Card>
              </Col>
            ))}
          </Row>
          <div>
            <h2 style={{ fontSize: "20px" }}>Premium Posting</h2>
            <Table
              rowKey="id"
              columns={premiumJobsColumn}
              dataSource={premiumSources}
              style={{ width: "100%", tableLayout: "auto" }}
              loading={loading}
            />
          </div>
          <div style={{ width: "100%" }}>
            <h1
              style={{
                marginBottom: "40px",
                fontSize: "20px",
                fontWeight: "bold",
              }}
            >
              The list of companies that will expire in the next 10 days
            </h1>
            <Table<DataType>
              columns={columns}
              rowKey="_id"
              onChange={onChange}
              dataSource={subscription}
              showSorterTooltip={{ target: "sorter-icon" }}
              rowClassName={() => "fixed-row"}
              style={{ width: "100%", wordWrap: "break-word" }}
            />
          </div>
          <div style={{ display: "flex", gap: "20px" }}>
            <div>
              <div style={{ display: "flex", gap: "40px" }}>
                <h1
                  style={{
                    marginBottom: "24px",
                    fontSize: "20px",
                    fontWeight: "bold",
                  }}
                >
                  Monthly applications statistics
                </h1>
                <Select
                  style={{
                    width: "120px",
                    marginTop: "20px",
                  }}
                  value={selectedYear}
                  onChange={(value) => handleMonthData(value)}
                  options={[
                    { value: "2020", label: "2020" },
                    { value: "2021", label: "2021" },
                    { value: "2022", label: "2022" },
                    { value: "2023", label: "2023" },
                    { value: "2024", label: "2024" },
                    { value: "2025", label: "2025" },
                  ]}
                />
              </div>
              <div style={{ width: "100%", height: "400px" }}>
                <Line
                  style={{ width: "80%", height: "400px" }}
                  data={monthsData}
                  options={options}
                />
              </div>
            </div>
            <div>
              <h1
                style={{
                  marginBottom: "24px",
                  fontSize: "20px",
                  fontWeight: "bold",
                }}
              >
                Yearly applications statistics
              </h1>
              <div style={{ width: "100%", height: "400px" }}>
                <Line
                  style={{ width: "70%", height: "400px" }}
                  data={yearsData}
                  options={options}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
